import { Analytics } from '@cobliteam/analytics';
import Icon, { type IconName } from '@cobliteam/colib-ui/Icon';
import Separator from '@cobliteam/colib-ui/Separator';
import type { RouteDef } from '@cobliteam/dashboard/src/dashboardDef.type';
import { useLanguage } from '@cobliteam/i18n';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import { AnimatePresence, m } from 'motion/react';
import { type RailDensity, railShowsTitle } from '../hooks/use-rail-density';
import LinkWrapper from './LinkWrapper';
import MostVisitedMenu from './MostVisitedMenu';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu as SidebarMenuBase,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarSecondaryLabel,
  SidebarTrigger,
  useSidebar,
} from './Sidebar';
import { SidebarRailItem } from './SidebarRailItem';

interface SidebarMenuProps {
  primary?: boolean;
  primaryDef: RouteDef[];
  primaryHovered?: RouteDef;
  showTitle?: boolean;
  density?: RailDensity;
  hoveredRoute?: RouteDef | null;
  onMouseEnter?: (route: RouteDef | null) => void;
  onMouseLeave?: () => void;
}
export function SidebarMenu({ primary, primaryDef, primaryHovered, hoveredRoute, onMouseEnter, onMouseLeave, showTitle = true, density = 'full' }: SidebarMenuProps) {
  const { setOpenMobile, isLaptop } = useSidebar();
  const { currentLanguage } = useLanguage();
  const handleClick = (route: RouteDef) => {
    const title = getTextForLanguage(route.sidebar?.title, currentLanguage);
    if (primary) {
      Analytics.track('Sidebar - Primary - Navigate', {
        destination: title,
      });
    } else {
      Analytics.track('Sidebar - Secondary - Navigate', {
        destination: title,
      });
    }
  };
  if (primaryDef && !primaryHovered) {
    return primaryDef.map((route) => {
      const title = getTextForLanguage(route.sidebar?.title, currentLanguage);
      return (
        <LinkWrapper
          to={route.redirect ?? '#'}
          pageId={route.id}
          className='group/primary !cursor-pointer flex flex-col items-center justify-center text-center text-muted-foreground'
          aria-label={railShowsTitle(density, showTitle) ? undefined : title}
          key={title}
          onMouseEnter={() => onMouseEnter?.(route)}
          onMouseLeave={() => onMouseLeave?.()}
          onClick={() => handleClick(route)}
        >
          <SidebarRailItem
            title={title}
            icon={route.sidebar?.icon as IconName | undefined}
            isAI={route.isAI}
            isActive={route.sidebar?.active}
            isHovered={route.id === hoveredRoute?.id}
            density={density}
            showTitle={showTitle}
          />
        </LinkWrapper>
      );
    });
  }
  return (
    <AnimatePresence mode='wait'>
      {primaryHovered && (
        <SidebarGroup>
          <div className='flex items-center justify-between shadow-sm'>
            <SidebarSecondaryLabel className='h-[55px]'>{getTextForLanguage(primaryHovered?.sidebar?.title, currentLanguage)}</SidebarSecondaryLabel>
            {!isLaptop && <SidebarTrigger />}
          </div>
          <Separator className='mb-2 bg-zinc-300 dark:bg-border' />
          <div className='space-y-0.5'>
            {primaryHovered?.routes?.map((item, index, items) => {
              const itemTitle = getTextForLanguage(item.sidebar?.title, currentLanguage);
              const itemIcon = item.sidebar?.icon || '';

              // component
              const component = item.component;
              if (component === 'most-visited-menu') return <MostVisitedMenu key={`${itemTitle}-${itemIcon}-${index}`} />;
              if (item.sidebar?.hide) return null;
              const hasGroup = item?.routes?.some((child) => !child.sidebar.hide);
              const previousItem = items[index - 1];
              // Item solto depois de um grupo ficaria visualmente dentro dele sem o divisor.
              const followsGroup = !!previousItem && !previousItem.sidebar?.hide && !!previousItem.routes?.some((child) => !child.sidebar.hide);
              return (
                <m.div
                  key={itemTitle + itemIcon}
                  initial={{
                    opacity: 0,
                    x: -10,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    x: 10,
                  }}
                  transition={{
                    type: 'tween',
                    damping: 25,
                    stiffness: 300,
                    duration: 0.4,
                  }}
                >
                  {hasGroup ? (
                    <SidebarGroup key={itemTitle}>
                      {index !== 0 && <Separator className='my-2 bg-zinc-300 dark:bg-border' />}
                      <SidebarGroupLabel className='!text-xs !text-sidebar-foreground font-medium'>{itemTitle}</SidebarGroupLabel>
                      <SidebarGroupContent>
                        <SidebarMenuSub className='translate-x-0 border-l-0 px-0'>
                          {item?.routes?.map((route) => {
                            if (route.sidebar?.hide) return null;
                            const goTo = route.redirect || route.url || '#';
                            const routeTitle = getTextForLanguage(route.sidebar?.title, currentLanguage);
                            const hasExternalLink = !!route.sidebar?.externalLink;
                            return (
                              <SidebarMenuItem key={routeTitle}>
                                <SidebarMenuButton
                                  isActive={route.sidebar?.active}
                                  onClick={() => setOpenMobile(false)}
                                  title={routeTitle}
                                  render={
                                    <LinkWrapper
                                      to={goTo}
                                      pageId={route.id}
                                      target={hasExternalLink ? '_blank' : '_self'}
                                      onClick={() =>
                                        Analytics.track('Sidebar - Navigate', {
                                          destination: routeTitle,
                                          group: itemTitle,
                                        })
                                      }
                                    >
                                      {route?.sidebar?.icon && <Icon size='sm' name={route.sidebar?.icon as IconName} className='size-4' />}
                                      <span className='flex-1'>{routeTitle}</span>
                                      {hasExternalLink && <Icon size='sm' name='external-link' className='size-4' />}
                                    </LinkWrapper>
                                  }
                                />
                              </SidebarMenuItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  ) : (
                    <SidebarGroup key={itemTitle}>
                      {followsGroup && <Separator className='my-2 bg-zinc-300 dark:bg-border' />}
                      <SidebarMenuBase>
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            isActive={item.sidebar?.active}
                            onClick={() => setOpenMobile(false)}
                            render={
                              <LinkWrapper
                                to={item.redirect || item.url || '#'}
                                pageId={item.id}
                                onClick={() =>
                                  Analytics.track('Sidebar - Navigate', {
                                    destination: itemTitle,
                                  })
                                }
                              >
                                {item?.sidebar?.icon && <Icon size='sm' name={item.sidebar?.icon as IconName} className='size-4' />}
                                <span className='flex-1'>{itemTitle}</span>
                              </LinkWrapper>
                            }
                          />
                        </SidebarMenuItem>
                      </SidebarMenuBase>
                    </SidebarGroup>
                  )}
                </m.div>
              );
            })}
          </div>
        </SidebarGroup>
      )}
    </AnimatePresence>
  );
}
